/**
 * First-party journey tracking: which screen a customer is on, what they open,
 * which toasts they tap. The events are listed in
 * admin-app/docs/user-journeys.md.
 *
 * Events queue in memory and go to /api/track-events every few seconds, and by
 * beacon when the tab is hidden or closed, so a closed tab doesn't lose them.
 * The server route adds the visitor, the profile and the country, and moves the
 * times onto the server's clock.
 *
 * Fire-and-forget: tracking must never break the page or block a navigation.
 * It never records message text or anything the customer types.
 */

const ENDPOINT = "/api/track-events";
const FLUSH_EVERY_MS = 5_000;
const MAX_BATCH = 50;
const MAX_QUEUE = 500;
const SESSION_IDLE_MS = 30 * 60_000;
const SESSION_KEY = "fb_journey_session";

export type JourneyProps = Record<string, string | number | boolean | null | undefined>;

interface QueuedEvent {
  id: string;
  event: string;
  screen?: string;
  props?: JourneyProps;
  occurred_at: string;
  session_id: string;
}

const queue: QueuedEvent[] = [];
let currentScreen: string | undefined;
let started = false;
let memorySession: { id: string; last: number } | null = null;
let lastEventAt = 0;

function newId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // RFC 4122 v4 from Math.random, for browsers without randomUUID.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * The event's time, kept strictly increasing so two events in the same
 * millisecond (a toast tap, then the screen it opens) keep their order.
 */
function eventTime(): string {
  lastEventAt = Math.max(Date.now(), lastEventAt + 1);
  return new Date(lastEventAt).toISOString();
}

/** One browser tab's session; a new one starts after 30 minutes idle. */
function sessionId(): string {
  const now = Date.now();
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    const prev = raw ? (JSON.parse(raw) as { id: string; last: number }) : null;
    const id = prev && now - prev.last < SESSION_IDLE_MS ? prev.id : newId();
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, last: now }));
    return id;
  } catch {
    // sessionStorage can be blocked: keep the session in memory instead.
    if (!memorySession || now - memorySession.last >= SESSION_IDLE_MS) {
      memorySession = { id: newId(), last: now };
    }
    memorySession.last = now;
    return memorySession.id;
  }
}

function send(batch: QueuedEvent[], beacon: boolean) {
  // sent_at lets the server correct for a device clock that is off.
  const body = JSON.stringify({ sent_at: new Date().toISOString(), events: batch });
  // A beacon survives the tab closing; fetch with keepalive is the fallback.
  if (beacon && navigator.sendBeacon) {
    if (navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }))) return;
  }
  void fetch(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {
    // Never surface a tracking failure.
  });
}

function flush(beacon = false) {
  while (queue.length) send(queue.splice(0, MAX_BATCH), beacon);
}

function start() {
  if (started) return;
  started = true;
  window.setInterval(() => flush(), FLUSH_EVERY_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      track("app_hidden");
      flush(true);
    } else {
      track("app_visible");
    }
  });
  window.addEventListener("pagehide", () => flush(true));
}

function withoutUndefined(props?: JourneyProps): JourneyProps | undefined {
  if (!props) return undefined;
  const out: JourneyProps = {};
  for (const [key, value] of Object.entries(props)) {
    if (value !== undefined) out[key] = value;
  }
  return out;
}

/** Pages shown inside an iframe (the admin app's landing-page previews) aren't visits. */
function isFramed(): boolean {
  try {
    return window.top !== window.self;
  } catch {
    return true;
  }
}

/** Records one event, on the current screen unless another is given. */
export function track(event: string, props?: JourneyProps, screen?: string): void {
  if (typeof window === "undefined") return;
  try {
    if (isFramed()) return;
    start();
    if (queue.length >= MAX_QUEUE) queue.shift();
    queue.push({
      id: newId(),
      event,
      screen: screen ?? currentScreen,
      props: withoutUndefined(props),
      occurred_at: eventTime(),
      session_id: sessionId(),
    });
    if (queue.length >= MAX_BATCH) flush();
  } catch {
    // Never surface a tracking failure.
  }
}

/** Records a screen change, and makes it the screen for the events after it. */
export function trackScreen(screen: string, props?: JourneyProps): void {
  currentScreen = screen;
  track("screen_view", props, screen);
}

/** The screen the customer is on, as last recorded. */
export function getCurrentScreen(): string | undefined {
  return currentScreen;
}
